import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"

import { useClarificationRequestModel } from "entities/clarification-requests/model/useClarificationRequestModel"
import { clarificationRequestResourceTypeMap } from "entities/clarification-requests/types/ClarificationRequest"
import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useProductPositionFragment } from "entities/product-positions/api/useProductPositionFragment"
import { useProductPositionMutations } from "entities/product-positions/api/useProductPositionMutations"
import { ProductPositionFormData } from "entities/product-positions/types/ProductPositionForm"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useProductPositionFormModel } from "widgets/product-position/product-position-form/model/useProductPositionFormModel"

import { EditProductPositionOverlayProps } from "../EditProductPositionOverlay"
import { EditProductPositionOverlayViewModel } from "../EditProductPositionOverlayView"

export function useEditProductPositionOverlayModel({
  payload: { projectID, productPositionID, defaultTab }
}: EditProductPositionOverlayProps): EditProductPositionOverlayViewModel {
  const { updateProductPosition } = useProductPositionMutations()
  const productPosition = useProductPositionFragment(productPositionID)

  const { closeOverlay } = useCurrentOverlay()

  const clarificationRequest = useClarificationRequestModel({
    resourceType: clarificationRequestResourceTypeMap.ProductPosition,
    id: productPositionID,
    projectID
  })

  const { parent } = useProjectChildPosition({
    child: productPosition || null,
    projectID
  })

  const submitProductPosition = useEvent(
    (formData: ProductPositionFormData) => {
      clarificationRequest.handlers.onSubmitContent?.(
        formData.clarificationRequest?.content
      )
      return updateProductPosition({
        projectID,
        parentID: formData.parentID,
        displayPosition: formData.displayPosition,
        productPosition: { ...formData.productPosition, id: productPositionID }
      })
    }
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<ProductPositionFormData>(
    {
      onSubmit: submitProductPosition,
      onSuccess: closeOverlay
    }
  )

  const productPositionFormModel = useProductPositionFormModel({
    id: "edit-product-position-overlay-form",
    defaultProjectID: projectID,
    productPosition: productPosition || null,
    isDefaultEditing: true,
    // Editing is controlled to `true`: this overlay closes on success via
    // useFormSubmit, so the form's own finishEditing() gate must never flip it
    // read-only (which would otherwise freeze the inputs when a submit fails).
    isEditing: true,
    clarificationRequest,
    onSubmit: handleSubmit,
    defaultParentID: parent?.id || projectID,
    formOptions: {
      hideInstallationReferenceSelectionWhileEditing: true,
      hideOpenProductConfigurationLinkWhileEditing: true,
      hideProject: true,
      // Favorites live in a virtual project; the attachments API rejects them
      // as parent (400 wrongParent).
      hideAttachments: projectID === FAVORITES_PROJECT_ID,
      defaultTab
    }
  })

  return {
    onClose: closeOverlay,
    productPositionFormModel,
    isLoading: isSubmitting,
    isSubmitting
  }
}
